import { Negotiation } from '@interfaces/models/negotiation/negotiation';
import logger from '@helpers/utils/logger/client';
import { datadogRum } from '@datadog/browser-rum-slim';
import { ListingsProduct, ListingsProductDetails, ListingsProducts } from '../domain/ListingsProducts';
import {
  ListingsItemPublishedDTO,
  ListingsItemUnpublishedMissingDetailsDTO,
  ListingsItemUnpublishedMissingPhotosDTO,
  ListingsItemUnpublishedSubmittedDTO,
  ListingsProductDetailsDTO,
  ListingsProductDTO,
  ListingsProductsDTO,
} from '../dtos/ListingsProductsDTO';

const mapProductData = (dataDTO: ListingsProductDTO): ListingsProduct => {
  const mapProductDetails = (productDTO: ListingsProductDetailsDTO): ListingsProductDetails => ({
    id: productDTO.id,
    headline: productDTO.headline,
    subHeadline: productDTO.subHeadline,
    picturePath: productDTO.picturePath,
    path: productDTO.path,
    price: productDTO.price.formatted,
    sellerEarnings: productDTO.sellerEarnings.formatted,
    inConsignment: ['CS pro', 'CS/VIP'].includes(productDTO.segment),
  });

  switch (dataDTO.type) {
    case 'unpublished-submitted': {
      const typedDTO = dataDTO as ListingsItemUnpublishedSubmittedDTO;
      return {
        type: 'unpublished-submitted',
        unpublishedSubmitted: {
          product: mapProductDetails(typedDTO.unpublishedSubmitted.product),
        },
      };
    }
    case 'unpublished-missing-details': {
      const typedDTO = dataDTO as ListingsItemUnpublishedMissingDetailsDTO;
      return {
        type: 'unpublished-missing-details',
        unpublishedMissingDetails: {
          product: mapProductDetails(typedDTO.unpublishedMissingDetails.product),
        },
      };
    }
    case 'unpublished-missing-photos': {
      const typedDTO = dataDTO as ListingsItemUnpublishedMissingPhotosDTO;
      return {
        type: 'unpublished-missing-photos',
        unpublishedMissingPhotos: {
          product: mapProductDetails(typedDTO.unpublishedMissingPhotos.product),
        },
      };
    }
    default: {
      const typedDTO = dataDTO as ListingsItemPublishedDTO;
      return {
        type: 'published',
        published: {
          product: mapProductDetails(typedDTO.published.product),
          listingDate: typedDTO.published.listingDate,
          negotiationsSummary: typedDTO.published.negotiationsSummary,
          singleNegotiationId: typedDTO.published.singleNegotiationId,
          hasPriceDropRecommendation: typedDTO.published.hasPriceDropRecommendation,
        },
      };
    }
  }
};

export const mapListingsProductsResponse = (dto: ListingsProductsDTO): ListingsProducts => {
  return {
    data: dto.data.map(mapProductData),
    meta: {
      limit: dto.meta.limit,
      offset: dto.meta.offset,
      count: dto.meta.count,
    },
  };
};

/**
 * Maps a Negotiation to a Listings product
 */
export const mapNegotiationToListing = (negotiation: Negotiation, listProduct: ListingsProduct): ListingsProduct => {
  let err: Error | null = null;

  if (!negotiation) {
    err = new Error('[mapNegotiationToListing]: Cannot map null or undefined negotiation');
  } else if (!listProduct) {
    err = new Error('[mapNegotiationToListing]: Cannot map negotiation without listProduct');
  } else if (!listProduct.type) {
    err = new Error('[mapNegotiationToListing]: Cannot map negotiation without type of listProduct');
  } else if (!listProduct[listProduct.type]) {
    err = new Error('[mapNegotiationToListing]: Cannot map negotiation without type Object of listProduct');
  }
  if (err) {
    datadogRum.addError(err);
    logger.error(err);
  }

  const { totalNegotiationsCount, ongoingNegotiationsCount, pendingSellerActionCount } =
    listProduct[listProduct.type].negotiationsSummary;

  const { sellerRemainingOffers, buyerRemainingOffers, isAccepted } = negotiation;

  return {
    type: listProduct.type,
    [listProduct.type]: {
      ...listProduct[listProduct.type],
      negotiationsSummary: {
        totalNegotiationsCount: totalNegotiationsCount,
        ongoingNegotiationsCount:
          !sellerRemainingOffers && !buyerRemainingOffers ? ongoingNegotiationsCount - 1 : ongoingNegotiationsCount,
        pendingSellerActionCount:
          sellerRemainingOffers > buyerRemainingOffers && !isAccepted
            ? pendingSellerActionCount
            : Math.max(pendingSellerActionCount - 1, 0),
      },
    },
  } as ListingsProduct;
};

//
